import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Paper, Typography } from '@mui/material';
import vibetideTheme from './theme/vibetideTheme';

// Lazy load della Home e delle pagine Dashboard
const HomePage = React.lazy(() => import('./pages/HomePage'));
const LayoutReal = React.lazy(() => import('./components/Layout/Layout'));
const DashboardReal = React.lazy(() => import('./pages/DashboardPage'));
const EventsPage = React.lazy(() => import('./pages/EventsPage'));
const EventSettingsPage = React.lazy(() => import('./pages/EventSettingsPage'));
const EventDisplayPage = React.lazy(() => import('./pages/EventDisplayPage'));
const MessagesPage = React.lazy(() => import('./pages/MessagesPage'));
const SponsorsPage = React.lazy(() => import('./pages/SponsorsPage'));
const PartecipaPage = React.lazy(() => import('./pages/PartecipaPage'));
const PartecipantiPage = React.lazy(() => import('./pages/PartecipantiPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const CookiePolicyPage = React.lazy(() => import('./pages/CookiePolicyPage'));
const PulseVotesPage = React.lazy(() => import('./pages/PulseVotesPage'));
const PulsePollsPage = React.lazy(() => import('./pages/PulsePollsPage'));
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const WhatsAppPage = React.lazy(() => import('./pages/WhatsAppPage'));

const SafePage = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Arial, sans-serif',
    background: '#0D1B2A',
    color: '#fff',
    padding: '24px',
    boxSizing: 'border-box'
  }}>
    <div style={{ textAlign: 'center', maxWidth: 720 }}>
      <img src="/logo_new.png" alt="Pulse" style={{ height: 60, marginBottom: 16 }} />
      <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 8, color: '#00E5FF' }}>Pulse is online</div>
      <div style={{ marginBottom: 16, opacity: 0.8 }}>
        La dashboard Pulse dedicata a votazioni e sondaggi live sta caricando.
      </div>
      <div style={{ fontSize: 14, opacity: 0.65 }}>
        Se vedi ancora questa schermata dopo l'aggiornamento, prova un hard refresh (Cmd/Ctrl + Shift + R).
      </div>
    </div>
  </div>
);

const DashboardSafe = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: '#0D1B2A' }}>
    <Paper elevation={2} sx={{ p: 4, maxWidth: 720, width: '100%', textAlign: 'center', borderRadius: 3 }}>
      <img src="/logo_new.png" alt="Pulse" style={{ height: 50, marginBottom: 12 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>Pulse Dashboard (SAFE MODE)</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        La dashboard dedicata a votazioni e sondaggi sarà disponibile a breve.
      </Typography>
      <Typography variant="caption" color="text.secondary">
        I tuoi dati di sessione sono al sicuro.
      </Typography>
    </Paper>
  </Box>
);

function OAuthCallbackSafe() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const token = params.get('token');
      const refreshToken = params.get('refreshToken');
      const userStr = params.get('user');

      // Salva in localStorage
      if (token) localStorage.setItem('authToken', token);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      let orgSlug = null;
      let userData = null;

      if (userStr) {
        try {
          userData = JSON.parse(decodeURIComponent(userStr));
          if (userData?.organization?.slug) {
            orgSlug = userData.organization.slug;
            localStorage.setItem('organizationSlug', orgSlug);
          }
          localStorage.setItem('userData', JSON.stringify(userData));

          // IMPORTANTE: Aggiorna anche lo store Zustand persistito
          const zustandState = {
            state: {
              user: userData,
              organization: userData?.organization || null,
              token: token,
              refreshToken: refreshToken,
              isAuthenticated: true
            },
            version: 0
          };
          localStorage.setItem('vibetide-auth', JSON.stringify(zustandState));
        } catch (_) { }
      }

      if (!orgSlug && token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload?.email) {
            orgSlug = payload.email.split('@')[0]
              .toLowerCase()
              .replace(/[^a-z0-9]/g, '-')
              .replace(/-+/g, '-')
              .replace(/^-|-$/g, '')
              .substring(0, 30);
            if (orgSlug) localStorage.setItem('organizationSlug', orgSlug);
          }
        } catch (_) { }
      }

      // Forza reload per far leggere i nuovi dati dallo store
      const dest = orgSlug ? `/${orgSlug}/dashboard` : '/dashboard';
      window.location.href = dest;
    } catch (e) {
      console.error('OAuth callback error:', e);
      navigate('/safe', { replace: true });
    }
  }, [location, navigate]);

  return <SafePage />;
}

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('ErrorBoundary:', error, info); }
  render() { return this.state.hasError ? <DashboardSafe /> : this.props.children; }
}

function App() {
  return (
    <ThemeProvider theme={vibetideTheme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Public */}
          <Route
            path="/"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SafePage />}>
                  <HomePage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route path="/login" element={
            <Suspense fallback={<SafePage />}>
              <LoginPage />
            </Suspense>
          } />
          <Route path="/safe" element={<SafePage />} />
          <Route path="/oauth-callback" element={<OAuthCallbackSafe />} />
          <Route path="/privacy-policy" element={
            <Suspense fallback={<SafePage />}>
              <PrivacyPolicyPage />
            </Suspense>
          } />
          <Route path="/terms-of-service" element={
            <Suspense fallback={<SafePage />}>
              <TermsOfServicePage />
            </Suspense>
          } />
          <Route path="/cookie-policy" element={
            <Suspense fallback={<SafePage />}>
              <CookiePolicyPage />
            </Suspense>
          } />

          {/* Pagina pubblica registrazione partecipanti */}
          <Route path="/partecipa/:eventoId" element={
            <Suspense fallback={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>Caricamento...</Box>}>
              <PartecipaPage />
            </Suspense>
          } />

          {/* Dashboard base */}
          <Route
            path="/dashboard"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <DashboardReal />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Sezioni Dashboard */}
          <Route
            path="/events"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <EventsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/votes"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <PulseVotesPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/polls"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <PulsePollsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/events/:eventId/settings"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <EventSettingsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/messages"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <MessagesPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/sponsors"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <SponsorsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/whatsapp"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <WhatsAppPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path="/partecipanti"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <PartecipantiPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Varianti multi-tenant con slug */}
          <Route
            path=":organizationSlug/dashboard"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <DashboardReal />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/events"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <EventsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/votes"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <PulseVotesPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/polls"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <PulsePollsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/events/:eventId/settings"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <EventSettingsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/messages"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <MessagesPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/sponsors"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <SponsorsPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/partecipanti"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <PartecipantiPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/whatsapp"
            element={
              <ErrorBoundary>
                <Suspense fallback={<DashboardSafe />}>
                  <LayoutReal>
                    <WhatsAppPage />
                  </LayoutReal>
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Display Routes */}
          <Route
            path="/display/event-display/:eventId"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SafePage />}>
                  <EventDisplayPage />
                </Suspense>
              </ErrorBoundary>
            }
          />
          <Route
            path=":organizationSlug/display/event-display/:eventId"
            element={
              <ErrorBoundary>
                <Suspense fallback={<SafePage />}>
                  <EventDisplayPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App; 
